import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Settings, 
  Menu, 
  X,
  Calendar,
  Briefcase
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useSidebar } from '../../contexts/SidebarContext';
import { useLanguage } from '../../contexts/LanguageContext';
import SidebarCollapseButton from './SidebarCollapseButton';
import LogoutButton from '../auth/LogoutButton';

const mainMenuItems = [
  { 
    icon: LayoutDashboard, 
    labelKey: 'navigation.dashboard', 
    descKey: 'navigation.dashboardDesc',
    path: '/dashboard'
  },
  { 
    icon: Briefcase, 
    labelKey: 'navigation.jobs', 
    descKey: 'navigation.jobsDesc',
    path: '/jobs'
  },
  { 
    icon: ClipboardList, 
    labelKey: 'navigation.applications', 
    descKey: 'navigation.applicationsDesc',
    path: '/candidates'
  },
  { 
    icon: Calendar, 
    labelKey: 'navigation.calendar', 
    descKey: 'navigation.calendarDesc',
    path: '/calendar'
  }
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const { t } = useLanguage();

  const isActiveRoute = (path: string) => location.pathname === path;

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed lg:sticky top-0 left-0 h-screen flex-shrink-0 z-40",
          "bg-gradient-to-b from-slate-900 to-slate-800",
          "transform transition-all duration-300 ease-in-out lg:translate-x-0",
          "shadow-xl",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          isCollapsed ? "w-20" : "w-[260px]"
        )}
      >
        <div className="flex flex-col h-full overflow-hidden relative">
          <div className={cn(
            "px-4 py-6 border-b border-slate-700/30",
            "transition-all duration-300"
          )}>
            <div className={cn(
              "flex",
              isCollapsed ? "justify-center items-center" : "justify-start items-center"
            )}>
              {isCollapsed ? (
                <img 
                  src="/images/Branco-Vertical.svg" 
                  alt="Aura" 
                  className="h-16 w-auto object-contain transition-all duration-300" 
                />
              ) : (
                <img 
                  src="/images/Branco-Horizontal.svg" 
                  alt="Aura" 
                  className="h-12 w-auto object-contain pl-2 transition-all duration-300" 
                />
              )}
            </div>
          </div>

          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            <div className="space-y-1">
              {mainMenuItems.map((item) => (
                <Link
                  key={item.labelKey}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200",
                    "hover:bg-white/10 active:scale-[0.98]",
                    isActiveRoute(item.path) 
                      ? "bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-white" 
                      : "text-slate-300",
                    isCollapsed && "justify-center"
                  )}
                  title={isCollapsed ? `${t(item.labelKey)}: ${t(item.descKey)}` : undefined}
                >
                  <div className={cn(
                    "w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0",
                    isActiveRoute(item.path) ? "bg-white/10" : "bg-transparent"
                  )}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  {!isCollapsed && (
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-sm whitespace-nowrap">{t(item.labelKey)}</span>
                      <span className="text-xs text-slate-400 truncate max-w-[180px]">{t(item.descKey)}</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </nav>

          <div className="p-3 mt-auto border-t border-slate-700/30">
            <div>
              <Link
                to="/settings"
                className={cn(
                  "flex items-center gap-3 px-3 py-3 rounded-lg text-slate-300 hover:bg-white/10 transition-colors",
                  isCollapsed && "justify-center"
                )}
                title={isCollapsed ? t('navigation.settings') : undefined}
              >
                <div className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0",
                  isActiveRoute("/settings") ? "bg-white/10" : "bg-transparent"
                )}>
                  <Settings className="w-5 h-5" />
                </div>
                {!isCollapsed && (
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium text-sm whitespace-nowrap">{t('navigation.settings')}</span>
                    <span className="text-xs text-slate-400 truncate max-w-[180px]">{t('navigation.settingsDesc')}</span>
                  </div>
                )}
              </Link>
            </div>
            
            <div className="mt-2 pb-1">
              <LogoutButton 
                className={cn(
                  "w-full",
                  isCollapsed && "justify-center"
                )}
                isCollapsed={isCollapsed}
              />
            </div>
            
          </div>

          <SidebarCollapseButton />
        </div>
      </aside>
    </>
  );
};

export default Sidebar;